import React, { useState, useMemo } from 'react'

export default function DataTable ({
  data = [],
  columns = [],
  searchable = false,
  searchPlaceholder = 'Cerca...',
  pagination = false,
  pageSize = 15,
  fillHeight = false,
  expandRow,
  emptyText = 'Nessun dato da visualizzare.',
  actions
}) {
  const [query, setQuery] = useState('')
  const [sortField, setSortField] = useState(null)
  const [sortOrder, setSortOrder] = useState('asc')
  const [currentPage, setCurrentPage] = useState(0)
  const [expandedRows, setExpandedRows] = useState({})

  const filtered = useMemo(() => {
    if (!query.trim()) return data
    const q = query.toLowerCase()
    return data.filter(row =>
      columns.some(col => {
        const val = typeof col.accessor === 'function' ? col.accessor(row) : row[col.accessor]
        return val && val.toString().toLowerCase().includes(q)
      })
    )
  }, [data, query, columns])

  const sorted = useMemo(() => {
    if (!sortField) return filtered
    const col = columns.find(c => (typeof c.accessor === 'string' ? c.accessor : c.key) === sortField)
    if (!col) return filtered

    return [...filtered].sort((a, b) => {
      const aVal = typeof col.accessor === 'function' ? col.accessor(a) : a[col.accessor]
      const bVal = typeof col.accessor === 'function' ? col.accessor(b) : b[col.accessor]
      if (aVal == null) return 1
      if (bVal == null) return -1
      const cmp = String(aVal).localeCompare(String(bVal), 'it', { numeric: true })
      return sortOrder === 'asc' ? cmp : -cmp
    })
  }, [filtered, sortField, sortOrder, columns])

  const totalPages = pagination ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1
  const paged = pagination ? sorted.slice(currentPage * pageSize, (currentPage + 1) * pageSize) : sorted

  function handleSort (col) {
    const field = typeof col.accessor === 'string' ? col.accessor : col.key
    if (!field) return
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  function toggleExpand (id) {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }))
  }

  return (
    <div className={fillHeight ? 'ui-datatable-fill' : undefined}>
      {(searchable || actions) && (
        <div className='ui-table-toolbar'>
          {searchable && (
            <input
              className='ui-table-search'
              value={query}
              placeholder={searchPlaceholder}
              onChange={e => { setQuery(e.target.value); setCurrentPage(0) }}
            />
          )}
          {actions && <div className='ui-table-actions'>{actions}</div>}
        </div>
      )}

      <div className='ui-table-wrap'>
        <table className='ui-table'>
          <thead>
            <tr>
              {expandRow && <th style={{ width: 40 }} />}
              {columns.map((col, i) => (
                <th
                  key={col.key || i}
                  style={{ width: col.width, cursor: col.sortable !== false ? 'pointer' : undefined, whiteSpace: 'nowrap' }}
                  onClick={() => col.sortable !== false && handleSort(col)}
                >
                  {col.header}
                  {sortField === (typeof col.accessor === 'string' ? col.accessor : col.key) && (
                    <span style={{ marginLeft: 4, fontSize: '0.7em' }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 && (
              <tr><td colSpan={columns.length + (expandRow ? 1 : 0)} style={{ textAlign: 'center', padding: 24, color: 'var(--sf-text-muted)' }}>{emptyText}</td></tr>
            )}
            {paged.map((row, rowIndex) => {
              const rowId = row._id || rowIndex
              const isExpanded = !!expandedRows[rowId]
              return (
                <React.Fragment key={rowId}>
                  <tr>
                    {expandRow && (
                      <td style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleExpand(rowId)}>
                        {isExpanded ? '▾' : '▸'}
                      </td>
                    )}
                    {columns.map((col, i) => {
                      const val = typeof col.accessor === 'function' ? col.accessor(row) : row[col.accessor]
                      return (
                        <td key={col.key || i} style={col.style}>
                          {col.render ? col.render(val, row) : (val ?? '-')}
                        </td>
                      )
                    })}
                  </tr>
                  {expandRow && isExpanded && (
                    <tr className='ui-table-expand-row'>
                      <td colSpan={columns.length + 1}>
                        {expandRow(row)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {pagination && sorted.length > 0 && (
        <div className='ui-table-pagination'>
          <span className='ui-table-pagination-info'>
            {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, sorted.length)} di {sorted.length}
          </span>
          <div className='ui-table-pagination-btns'>
            <button disabled={currentPage === 0} onClick={() => setCurrentPage(0)}>«</button>
            <button disabled={currentPage === 0} onClick={() => setCurrentPage(p => p - 1)}>‹</button>
            <span>{currentPage + 1} / {totalPages}</span>
            <button disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(p => p + 1)}>›</button>
            <button disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(totalPages - 1)}>»</button>
          </div>
        </div>
      )}
    </div>
  )
}
